import { computed, action, observable } from 'mobx';
import { StoreBase } from '../../../common/StoreBase';
import { IAssuranceConfiguration } from '../models';
import { IImagePickerProps } from '@kurtosys/ksys-app-components/dist/components/base/ImagePicker/models';
import { IImagePickerImage } from '@kurtosys/ksys-app-components/dist/components/base/ImagePicker/models/IImagePickerImage';
import { IRequestOptions } from '@kurtosys/ksys-api-client/dist/models/IRequestOptions';
import { TAssuranceImagesRequestBody } from '@kurtosys/ksys-api-client/dist/models/requests/auth/TAssuranceImagesRequestBody';
import { IAssuranceImage } from '@kurtosys/ksys-api-client/dist/models/requests/auth/IAssuranceImage';
import { TValidateCodeResponseBody } from '@kurtosys/ksys-api-client/dist/models/requests/auth/TValidateCodeResponseBody';
import { ILabelledInputProps } from '@kurtosys/ksys-app-components/dist/components/base/LabelledInput/models/ILabelledInputProps';
import { utils } from '@kurtosys/ksys-app-template';

const { isNullOrEmpty } = utils.typeChecks;

const MAX_MESSAGE_LENGTH = 128;
const BASE64_PNG_PREFIX = 'data:image/png;base64,';
export class AssuranceStore extends StoreBase {
	static componentKey: 'assurance' = 'assurance';
	@observable.ref selectedImage: IImagePickerImage | undefined;
	@observable.ref codeValidation: TValidateCodeResponseBody | undefined = undefined;
	@observable public assuranceImageId = '';
	@observable.ref images: IImagePickerImage[] = [];
	@observable public messageRequired: boolean = false;
	@observable public assuranceMessage = '';
	@observable public assuranceImage = '';
	@observable public imageRequired: boolean = false;

	@computed
	get configuration(): IAssuranceConfiguration | undefined {
		if (this.storeContext && this.storeContext.appStore) {
			return this.storeContext.appStore.getComponentConfiguration(AssuranceStore.componentKey);
		}
	}

	@action
	async initialize(): Promise<void> {
	}

	@computed
	get saveAssuranceButtonText(): string {
		return (this.configuration && this.configuration.saveAssuranceButtonText) ||
			'Save Assurance';
	}

	@computed
	get assuranceMessageLabelText(): string {
		return (this.configuration && this.configuration.assuranceMessageLabelText) ||
			'Assurance Message';
	}

	@computed
	get assuranceImagePickerLabelText(): string {
		return (this.configuration && this.configuration.assuranceImagePickerLabelText) ||
			'Assurance Image';
	}

	@computed
	get successMessage(): string {
		return (this.configuration && this.configuration.successMessage) ||
			'Successfully updated assurance';
	}

	@computed
	get errorMessage(): string {
		return (this.configuration && this.configuration.errorMessage) ||
			'Could not update assurance';
	}

	@computed
	get errorAssuranceMessageRequired(): string {
		return (this.configuration && this.configuration.errorAssuranceMessageRequired) ||
			'Assurance message is required.';
	}

	@computed
	get errorMessageAssuranceMessage(): string {
		return (this.configuration && this.configuration.errorMessageAssuranceMessage) ||
			'Could not update assurance - Invalid assurance message';
	}

	@computed
	get errorMessageSelectedImage(): string {
		return (this.configuration && this.configuration.errorMessageSelectedImage) ||
			'Could not update assurance - Please select an image';
	}

	@computed
	get isValid(): boolean {
		return !!((!this.imageRequired || this.selectedImage) && (!this.messageRequired || this.messageValid));
	}

	@computed
	get messageValid() {
		return !isNullOrEmpty(this.assuranceMessage) && this.assuranceMessage.length <= MAX_MESSAGE_LENGTH;
	}

	@action
	getAssuranceImages = async () => {
		const { kurtosysApiStore } = this.storeContext;
		let rawImages: IAssuranceImage[] = [];
		const overrideOptions: Partial<IRequestOptions<TAssuranceImagesRequestBody>> = {
			body: {
				limit: this.imageLimit,
			},
			disableCachableRequests: true,
		};
		const assuranceImagesResponse = await kurtosysApiStore.assuranceImages.execute(overrideOptions);
		if (assuranceImagesResponse && Array.isArray(assuranceImagesResponse.images)) {
			rawImages = assuranceImagesResponse.images;
		}
		const BASE64_PNG_PREFIX = 'data:image/png;base64,';
		const images = rawImages.map((rawImage) => {
			let src = rawImage.image;
			if (!src.startsWith(BASE64_PNG_PREFIX)) {
				src = BASE64_PNG_PREFIX + src;
			}
			return {
				src,
				id: rawImage.imageId,
			} as IImagePickerImage;
		});
		if (!this.selectedImage && images.length > 0) {
			this.selectedImage = images[0];
		}
		this.images = images;
	}

	@computed
	get imageLimit(): number {
		if (this.configuration && typeof this.configuration.imageLimit === 'number') {
			return this.configuration.imageLimit;
		}
		return 9;
	}

	@action
	onImageSelect = (image: IImagePickerImage) => {
		if (image) {
			this.selectedImage = image;
		}
	}

	@action
	onImagePickerRefresh = () => {
		this.getAssuranceImages();
	}

	@action
	onChangeAssuranceMessage = (event: React.ChangeEvent<HTMLInputElement>) => {
		const value = event.target.value;
		if (value.length <= MAX_MESSAGE_LENGTH) {
			this.assuranceMessage = value;
		}
	}

	@action
	updateAssurances = async () => {
		const { kurtosysApiStore, appStore, messageStore } = this.storeContext;
		const { componentKey } = AssuranceStore;
		messageStore.clearAll(componentKey);
		if (this.isValid && this.selectedImage) {
			const loadingKey = 'SetupAssurancesStore.updateAssurances';
			appStore.startLoading(loadingKey);
			const response = await kurtosysApiStore.assuranceUpdate.callApi({
				body: {
					imageId: this.selectedImage.id,
					message: this.assuranceMessage,
				},
			});
			if (response.ok) {
				messageStore.setInfoText(this.successMessage, componentKey);
			}
			else {
				messageStore.setErrorText(this.errorMessage, componentKey);
			}
			appStore.stopLoading(loadingKey);
		}
		else if (this.isValid) {
			messageStore.setErrorText(this.errorMessageSelectedImage, componentKey);
		}
		else {
			messageStore.setErrorText(this.errorMessageAssuranceMessage, componentKey);
		}
	}

	@action
	setAssurance = (response: any) => {
		if (response) {
			this.imageRequired = response.assuranceImageEnabled;
			this.messageRequired = response.assuranceMessageEnabled;
			this.assuranceMessage = response.message;
			this.assuranceImageId = response.imageId;
			this.assuranceImage = BASE64_PNG_PREFIX + response.image;
		}
		const image = {
			src: this.assuranceImage,
			id: this.assuranceImageId,
		} as IImagePickerImage;

		this.onImageSelect(image);
		this.getAssuranceImages();
	}

	@computed
	get messageRemainingCharactersCount(): number {
		return MAX_MESSAGE_LENGTH - (this.assuranceMessage || '').length;
	}

	@computed
	get messageRemainingCharactersText(): string {
		return (this.configuration && this.configuration.messageRemainingCharactersText) ||
			'{count} characters remaining';
	}

	@computed
	get messagePlaceholderText() {
		return (this.configuration && this.configuration.messagePlaceholderText) ||
			'Assurance message';
	}

	validateMessage = (value: string) => {
		if (this.messageRequired && isNullOrEmpty(value)) {
			return { isValid: false, message: this.errorAssuranceMessageRequired };
		}
		return { isValid: true };
	}

	@computed
	get messageLabelledInputProps(): ILabelledInputProps {
		return {
			labelProps: {
				value: this.assuranceMessageLabelText,
			},
			inputProps: {
				value: this.assuranceMessage,
				onChange: this.onChangeAssuranceMessage,
				placeholder: this.messagePlaceholderText,
			},
			inputValidation: this.validateMessage,
		};
	}


	@computed
	get labelProps() {
		return {
			value: this.assuranceImagePickerLabelText,
		};
	}

	@computed
	get translateProps() {
		return {
			count: this.messageRemainingCharactersCount,
		};
	}

	@computed
	get imagePickerProps(): IImagePickerProps {
		const props: IImagePickerProps = {
			hide: !this.imageRequired,
			onSelect: this.onImageSelect,
			onRefresh: this.onImagePickerRefresh,
			images: this.images,
			selected: this.selectedImage,
		};
		return props;
	}

	public checkUsernameForAssurance = async (userName: string) => {
		const { assuranceStore, kurtosysApiStore } = this.storeContext;
		const overrideOptions = {
			body: {
				username: userName,
			},
		};
		const response = await kurtosysApiStore.assurance.execute(overrideOptions);
		assuranceStore.setAssurance(response);
	}
}